/** @jsxImportSource @emotion/react */
import * as authSelectors from "../auth/authSelectors";
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { Divider } from "../../components/Divider";
import { useGetMenuOverwrites } from "./service/menus-api";
import { SpinnerBlock } from "../common/Spinner";
import { css } from "@emotion/react";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { EmptyState } from "../../components/EmptyState";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { OverwriteMenuSelector } from "./components/OverwriteMenuSelector";
import { EditMenuOverwriteSelector } from "./components/EditMenuOverwriteSelector";
import { useHandleFetchError } from "../../error-handler";
import { InfoBanner } from "../../components/InfoBanner";

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const additionalCardStyle = css`
  flex-direction: row;
  padding-top: 24px;
  padding-bottom: 24px;
  align-items: center;
  justify-content: space-between;
`;

const listContainerAdditionalStyle = css`
  padding-top: 24px;
  padding-bottom: 24px;
  gap: 16px;
`;

const spanStyle = css`
  display: inline-block;
  margin-right: 4px;
`;

export const MenuMenuAvailabilityUpsert = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const {
    data: menuOverwrites,
    isLoading,
    error: getMenuOverwritesError,
  } = useGetMenuOverwrites(organizationKeyName, id);

  useHandleFetchError(getMenuOverwritesError);

  if (isLoading) {
    return <SpinnerBlock />;
  }

  return (
    <PageWrapper>
      <CardContainer>
        <PageHeader
          title="Menu schedules"
          subtitle="Change the menu at specific days and specific times as you like."
        />

        <div css={wrapperStyle}>
          <Divider />
          <CardBody css={additionalCardStyle}>
            <InfoBanner>
              <span className="bodyTextSemibold" css={spanStyle}>
                Menu scheduling:
              </span>
              Your changes will not impact your opening hours or restaurant
              capacity. You can change the menu at specific periods. Menu
              schedules can be changes to what products are on the menu, what
              the guest can see, what the prices are and if any discount.
            </InfoBanner>
          </CardBody>
          <Divider />
          <CardBody css={additionalCardStyle}>
            <StyledText
              className="bodyHeaderOneSemi mb-0"
              color={colors.textPrimary}
            >
              Menu schedules
            </StyledText>
            <OverwriteMenuSelector />
          </CardBody>
          <Divider />
          <CardBody css={listContainerAdditionalStyle}>
            {menuOverwrites?.length === 0 ? (
              <EmptyState
                title="No menu schedules found"
                subtitle="Create a menu schedule to control the availability of menus at specific times."
              />
            ) : (
              <>
                {menuOverwrites?.map((menuOverwrite, i) => (
                  <EditMenuOverwriteSelector
                    key={i}
                    overwrite={menuOverwrite}
                  />
                ))}
              </>
            )}
          </CardBody>
        </div>
      </CardContainer>
    </PageWrapper>
  );
};
